import { Skull, Info } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { SURVIVOR_BLOCKED, SURVIVOR_BLOCK_NOTE } from './types';
import type { SurvivorConfig } from './types';

/**
 * Survivor side-game configuration block.
 *
 * end_week is bounded by `regularSeasonEndWeek` (= playoff_start_week - 1
 * in the live data). Phase 4 wires the live league for the bound; for now
 * the parent passes whatever bound it wants validated.
 */
export function SurvivorBlock({
  config,
  onChange,
  regularSeasonEndWeek,
  isCommissioner,
}: {
  config: SurvivorConfig;
  onChange: (next: SurvivorConfig) => void;
  regularSeasonEndWeek: number;
  isCommissioner: boolean;
}) {
  const update = (patch: Partial<SurvivorConfig>) =>
    onChange({ ...config, ...patch });

  return (
    <div
      className={cn(
        'rounded-2xl p-5 sm:p-6 transition-colors',
        config.enabled
          ? 'border border-red-500/30 bg-gradient-to-br from-red-500/5 to-transparent'
          : 'border border-border bg-card',
      )}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3.5 min-w-0 flex-1">
          <div
            className={cn(
              'w-12 h-12 rounded-xl flex items-center justify-center shrink-0',
              config.enabled
                ? 'bg-red-500/15 text-red-400'
                : 'bg-muted text-muted-foreground',
            )}
          >
            <Skull className="w-6 h-6" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="font-bold text-lg">Survivor Mode</div>
              <Badge
                variant="outline"
                className="text-[9px] tracking-wider uppercase bg-purple-500/10 text-purple-300 border-purple-500/30 h-5"
              >
                Side Game
              </Badge>
            </div>
            <div className="text-[13px] text-muted-foreground mt-1 leading-relaxed">
              Each week, the lowest-scoring active team is eliminated. Last team
              standing wins the bonus. Eliminated teams continue normal H2H play.
            </div>
            {SURVIVOR_BLOCKED && (
              <div className="text-[12px] text-orange-400/90 mt-2 leading-relaxed">
                Survivor is {SURVIVOR_BLOCK_NOTE}
              </div>
            )}
          </div>
        </div>
        {/* SAB-632 STEP 0: Survivor is blocked until SAB-648 ships process-survivor-elimination payout. */}
        <Switch
          checked={config.enabled && !SURVIVOR_BLOCKED}
          onCheckedChange={(v) => { if (!SURVIVOR_BLOCKED) update({ enabled: v }); }}
          aria-label="Enable Survivor mode"
          disabled={!isCommissioner || SURVIVOR_BLOCKED}
        />
      </div>

      {config.enabled && (
        <div className="mt-5 pt-5 border-t border-border grid grid-cols-1 sm:grid-cols-3 gap-4">
          <FieldRow label="Start week">
            <Input
              type="number"
              min={1}
              max={Math.max(1, config.end_week - 1)}
              value={config.start_week}
              onChange={(e) =>
                update({ start_week: Math.max(1, parseInt(e.target.value) || 1) })
              }
              disabled={!isCommissioner}
            />
          </FieldRow>
          <FieldRow
            label="End week"
            sub={`Must be ≤ regular season end (wk ${regularSeasonEndWeek})`}
          >
            <Input
              type="number"
              min={config.start_week + 1}
              max={regularSeasonEndWeek}
              value={config.end_week}
              onChange={(e) =>
                update({
                  end_week: Math.min(
                    regularSeasonEndWeek,
                    parseInt(e.target.value) || regularSeasonEndWeek,
                  ),
                })
              }
              disabled={!isCommissioner}
            />
          </FieldRow>
          <FieldRow label="Bonus amount ($)">
            <Input
              type="number"
              min={0}
              step={1}
              value={config.bonus_amount}
              onChange={(e) =>
                update({
                  bonus_amount: Math.max(0, parseFloat(e.target.value) || 0),
                })
              }
              disabled={!isCommissioner}
            />
          </FieldRow>

          <div className="sm:col-span-3 px-3.5 py-3 bg-muted rounded-lg text-xs text-muted-foreground flex gap-2.5 items-start">
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-orange-500" />
            <div className="leading-relaxed">
              <strong className="text-foreground">Tiebreaker:</strong> If two teams
              tie for lowest score, the one with more cumulative points-against
              survives (already punished by H2H, don't double-punish).
              <br />
              <strong className="text-foreground">Bye weeks:</strong> Teams on bye
              are safe. Only active teams are eligible for elimination that
              week.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FieldRow({
  label,
  sub,
  children,
}: {
  label: string;
  sub?: string;
  children: React.ReactNode;
}) {
  // Helper text renders BELOW the input so all column inputs share the same
  // vertical baseline. Putting `sub` between label and input pushed End Week
  // down a row when adjacent columns had no sub.
  return (
    <label className="flex flex-col gap-1.5">
      <Label className="text-[11px] font-bold uppercase tracking-[0.06em] text-muted-foreground">
        {label}
      </Label>
      {children}
      {sub && (
        <div className="text-[11px] text-muted-foreground/70 leading-snug">
          {sub}
        </div>
      )}
    </label>
  );
}
